import React, { useState, useEffect } from 'react';
import { useCloudAuth } from '../../context/CloudAuthContext';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import { 
  DollarSign, Clock, Phone, CreditCard, TrendingUp,
  CheckCircle, XCircle, AlertCircle, Loader2, Zap, Gift, Calendar,
  Building2, PhoneCall, Play, Pause, RefreshCcw, FileText, Wallet
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VoiceBilling() {
  const { fetchApi } = useCloudAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [billingSummary, setBillingSummary] = useState(null);
  const [profilesSummary, setProfilesSummary] = useState(null);
  const [status, setStatus] = useState(null);
  const [autopayStatus, setAutopayStatus] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [error, setError] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [activatingProfile, setActivatingProfile] = useState(null);
  const [settingUpAutopay, setSettingUpAutopay] = useState(false);

  useEffect(() => {
    loadBillingData();
    
    // Handle subscription success callbacks
    const subscriptionStatus = searchParams.get('subscription');
    const profileSubscriptionStatus = searchParams.get('profile_subscription');
    const autopayParam = searchParams.get('autopay');
    const sessionId = searchParams.get('session_id');
    const profileId = searchParams.get('profile_id');
    
    if (subscriptionStatus === 'success' && sessionId) {
      pollPaymentStatus(sessionId);
    } else if (profileSubscriptionStatus === 'success' && sessionId && profileId) {
      pollProfilePaymentStatus(profileId, sessionId);
    } else if (subscriptionStatus === 'cancelled' || profileSubscriptionStatus === 'cancelled') {
      toast.error('Payment was cancelled');
    } else if (autopayParam === 'success') {
      toast.success('Autopay setup successful! Your card is now saved for automatic billing.');
      loadBillingData();
    } else if (autopayParam === 'cancelled') {
      toast.error('Autopay setup was cancelled');
    }
  }, []);

  const loadBillingData = async () => {
    try {
      setLoading(true);
      const [billingRes, statusRes, profilesRes, autopayRes, invoicesRes] = await Promise.all([
        fetchApi('/api/cloud/billing/billing-cycle'),
        fetchApi('/api/cloud/billing/subscription/status').catch(() => ({ data: null })),
        fetchApi('/api/cloud/billing/profiles/summary'),  // New endpoint with per-profile status
        fetchApi('/api/cloud/billing/autopay/status').catch(() => ({ data: null })),
        fetchApi('/api/cloud/billing/invoices?limit=5').catch(() => ({ data: [] }))
      ]);
      
      setBillingSummary(billingRes.data);
      if (statusRes.data) {
        setStatus(statusRes.data);
      }
      if (profilesRes.data) {
        setProfilesSummary(profilesRes.data);
      }
      if (autopayRes.data) {
        setAutopayStatus(autopayRes.data);
      }
      if (invoicesRes.data) {
        setInvoices(invoicesRes.data);
      }
    } catch (err) {
      setError('Failed to load billing data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please refresh.');
      return;
    }

    try {
      setProcessingPayment(true);
      const response = await fetchApi(`/api/cloud/billing/checkout/status/${sessionId}`);
      
      if (response.data?.payment_status === 'paid') {
        toast.success('Subscription activated!');
        loadBillingData();
        setProcessingPayment(false);
        return;
      } else if (response.data?.status === 'expired') {
        toast.error('Payment session expired.');
        setProcessingPayment(false);
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment:', error);
      setProcessingPayment(false);
    }
  };

  const pollProfilePaymentStatus = async (profileId, sessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please refresh.');
      setActivatingProfile(null);
      return;
    }

    try {
      setActivatingProfile(profileId);
      const response = await fetchApi(`/api/cloud/billing/profile/${profileId}/checkout/status/${sessionId}`);
      
      if (response.data?.payment_status === 'paid') {
        toast.success('Business profile subscription activated!');
        loadBillingData();
        setActivatingProfile(null);
        return;
      } else if (response.data?.status === 'expired') {
        toast.error('Payment session expired.');
        setActivatingProfile(null);
        return;
      }
      setTimeout(() => pollProfilePaymentStatus(profileId, sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking profile payment:', error);
      setActivatingProfile(null);
    }
  };

  const activateProfileSubscription = async (profileId, profileName) => {
    try {
      setActivatingProfile(profileId);
      const response = await fetchApi(`/api/cloud/billing/profile/${profileId}/activate`, {
        method: 'POST',
        body: JSON.stringify({ originUrl: window.location.origin })
      });
      
      if (response.data?.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      } else {
        throw new Error('No checkout URL');
      }
    } catch (error) {
      console.error('Failed to activate profile subscription:', error);
      toast.error(`Failed to activate subscription for ${profileName}`);
      setActivatingProfile(null);
    }
  };

  const cancelProfileSubscription = async (profileId, profileName) => {
    if (!window.confirm(`Cancel subscription for "${profileName}"? The profile will revert to trial mode.`)) return;
    
    try {
      await fetchApi(`/api/cloud/billing/profile/${profileId}/cancel`, { method: 'POST' });
      toast.success(`Subscription cancelled for ${profileName}`);
      loadBillingData();
    } catch (error) {
      toast.error('Failed to cancel subscription');
    }
  };

  const activateSubscription = async () => {
    try {
      setProcessingPayment(true);
      const response = await fetchApi('/api/cloud/billing/subscription/activate', {
        method: 'POST',
        body: JSON.stringify({ originUrl: window.location.origin })
      });
      
      if (response.data?.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      } else {
        throw new Error('No checkout URL');
      }
    } catch (error) {
      console.error('Failed to activate:', error);
      toast.error('Failed to start subscription');
      setProcessingPayment(false);
    }
  };

  const cancelSubscription = async () => {
    if (!window.confirm('Cancel subscription? You will lose access after the current period.')) return;
    
    try {
      await fetchApi('/api/cloud/billing/subscription/cancel', { method: 'POST' });
      toast.success('Subscription cancelled');
      loadBillingData();
    } catch (error) {
      toast.error('Failed to cancel');
    }
  };

  const makePayment = async () => {
    try {
      setProcessingPayment(true);
      const response = await fetchApi('/api/cloud/billing/pay-invoice', {
        method: 'POST',
        body: JSON.stringify({ originUrl: window.location.origin })
      });
      
      if (response.data?.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      }
    } catch (error) {
      toast.error('Failed to initiate payment');
      setProcessingPayment(false);
    }
  };

  const setupAutopay = async () => {
    try {
      setSettingUpAutopay(true);
      const response = await fetchApi('/api/cloud/billing/autopay/setup', {
        method: 'POST',
        body: JSON.stringify({})
      });
      
      if (response.data?.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Failed to setup autopay:', error);
      toast.error('Failed to setup autopay');
      setSettingUpAutopay(false);
    }
  };

  const disableAutopay = async () => {
    if (!window.confirm('Disable autopay? You will need to pay invoices manually each month.')) return;
    
    try {
      await fetchApi('/api/cloud/billing/autopay/disable', { method: 'POST' });
      toast.success('Autopay disabled');
      loadBillingData();
    } catch (error) {
      toast.error('Failed to disable autopay');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Loading billing data...</div>
      </div>
    );
  }

  if (error) {
    return <div className="text-red-400 text-center py-8">{error}</div>;
  }

  // Extract status info
  const subscription = status?.subscription || {};
  const trial = status?.trial || {};
  const billing = status?.billing || {};
  const pricing = status?.pricing || {};
  const agentActive = status?.agentActive ?? true;
  const deactivationReason = status?.deactivationReason;
  
  // Profile-based billing
  const profiles = profilesSummary?.profiles || [];
  const activeSubscriptions = profilesSummary?.activeSubscriptions || 0;
  const totalSubscriptionCost = profilesSummary?.totalSubscriptionCost || 0;
  const totalPhoneCost = profilesSummary?.totalPhoneCost || 0;
  const subscriptionPricePerProfile = profilesSummary?.subscriptionPricePerProfile || 10;
  
  const isActive = activeSubscriptions > 0;
  const freeMinutes = pricing.freeMinutesIncluded || 10;
  const pricePerMinute = pricing.pricePerMinute || 0.20;
  
  const summary = billingSummary || {};
  const totalMinutes = summary.totalMinutes || 0;
  const freeMinutesUsed = Math.min(totalMinutes, freeMinutes);
  const billableMinutes = Math.max(0, totalMinutes - freeMinutes);
  const usageCost = billableMinutes * pricePerMinute;
  const totalBill = totalSubscriptionCost + totalPhoneCost + usageCost;
  
  // Billing cycle info
  const billingCycleActive = summary.billingCycleActive;
  const cycleStart = summary.cycleStart ? new Date(summary.cycleStart) : null;
  const cycleEnd = summary.cycleEnd ? new Date(summary.cycleEnd) : null;
  const daysRemaining = summary.daysRemaining || 0;
  const daysElapsed = summary.daysElapsed || 0;

  // Format date helper
  const formatDate = (date) => {
    if (!date) return '-';
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  return (
    <div className="space-y-6" data-testid="voice-billing">
      {/* Billing Period Banner */}
      {billingCycleActive && cycleStart && cycleEnd && (
        <div className="bg-gradient-to-r from-purple-900/40 to-blue-900/40 border border-purple-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <div className="text-sm text-gray-400">Current Billing Period</div>
                <div className="text-lg font-semibold text-white">
                  {formatDate(cycleStart)} — {formatDate(cycleEnd)}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-white">{daysRemaining}</div>
              <div className="text-sm text-gray-400">days remaining</div>
            </div>
          </div>
          <div className="mt-3">
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>Day {daysElapsed} of 30</span>
              <span>{Math.round((daysElapsed / 30) * 100)}% complete</span>
            </div>
            <div className="w-full bg-gray-700 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full transition-all"
                style={{ width: `${Math.min(100, (daysElapsed / 30) * 100)}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Consolidated Billing Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-400" />
            Consolidated Billing
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            {profiles.length} business profile{profiles.length !== 1 ? 's' : ''} • {activeSubscriptions} active subscription{activeSubscriptions !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-4 text-sm text-gray-400">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            {profiles.length} profiles
          </div>
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4" />
            <Phone className="w-4 h-4" />
            {totalPhoneCost > 0 ? `$${totalPhoneCost.toFixed(2)}/mo phones` : 'No phones'}
          </div>
        </div>
      </div>

      {/* Business Profiles Subscription Status */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Building2 className="h-5 w-5 text-purple-400" />
            Business Profiles - Subscription Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {profiles.map((profile) => (
              <div 
                key={profile.id}
                className={`p-4 rounded-lg border ${
                  profile.subscription.isActive 
                    ? 'bg-green-900/10 border-green-500/30' 
                    : profile.trial.exhausted 
                      ? 'bg-red-900/10 border-red-500/30'
                      : 'bg-gray-800/50 border-gray-700'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${
                      profile.subscription.isActive ? 'bg-green-400' : 
                      profile.trial.exhausted ? 'bg-red-400' : 'bg-yellow-400'
                    }`} />
                    <div>
                      <div className="font-medium text-white flex items-center gap-2">
                        {profile.name}
                        {profile.isDefault && (
                          <span className="text-xs px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded">Default</span>
                        )}
                      </div>
                      <div className="text-sm text-gray-400">
                        {profile.subscription.isActive ? (
                          <>
                            Active subscription • ${subscriptionPricePerProfile}/month
                            {profile.subscription.periodEnd && (
                              <span className="ml-2 text-purple-400">
                                • Renews {formatDate(new Date(profile.subscription.periodEnd))}
                              </span>
                            )}
                          </>
                        ) : profile.trial.exhausted ? (
                          <>Trial exhausted • Subscription required</>
                        ) : (
                          <>Trial: {profile.trial.callsRemaining}/{profile.trial.maxCalls} calls remaining</>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {profile.phoneNumbers > 0 && (
                      <span className="text-sm text-gray-400">
                        <Phone className="w-3 h-3 inline mr-1" />
                        {profile.phoneNumbers} • ${profile.phoneNumberCost.toFixed(2)}/mo
                      </span>
                    )}
                    {profile.subscription.isActive ? (
                      <button
                        onClick={() => cancelProfileSubscription(profile.id, profile.name)}
                        className="px-3 py-1.5 text-sm bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                    ) : (
                      <button
                        onClick={() => activateProfileSubscription(profile.id, profile.name)}
                        disabled={activatingProfile === profile.id}
                        className="px-3 py-1.5 text-sm bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors flex items-center gap-1 disabled:opacity-50"
                      >
                        {activatingProfile === profile.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Zap className="w-3 h-3" />
                        )}
                        Activate ${subscriptionPricePerProfile}/mo
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Trial progress bar for non-subscribed profiles */}
                {!profile.subscription.isActive && !profile.trial.exhausted && (
                  <div className="mt-3">
                    <div className="w-full bg-gray-700 rounded-full h-1.5">
                      <div 
                        className="bg-yellow-500 h-1.5 rounded-full transition-all"
                        style={{ width: `${(profile.trial.callsUsed / profile.trial.maxCalls) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* Summary */}
          <div className="mt-4 pt-4 border-t border-gray-800 flex justify-between items-center">
            <div className="text-sm text-gray-400">
              {activeSubscriptions} of {profiles.length} profiles subscribed
            </div>
            <div className="text-white font-medium">
              Subscriptions: ${totalSubscriptionCost.toFixed(2)}/month
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Agent Status Alert */}
      {!agentActive && (
        <Card className="bg-red-900/30 border-red-500/50">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <XCircle className="w-6 h-6 text-red-400" />
              <div>
                <div className="font-medium text-red-400">Voice Agent Disabled</div>
                <div className="text-sm text-gray-400">
                  {deactivationReason === 'trial_exhausted' && 
                    'Your free trial has ended. Subscribe to continue using Voice Agent.'}
                  {deactivationReason === 'unpaid_bill_overdue' && 
                    'Your account has an overdue bill. Please make a payment to reactivate.'}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Grace Period Warning */}
      {billing.hasUnpaidBill && !billing.gracePeriodExpired && (
        <Card className="bg-yellow-900/20 border-yellow-500/30">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-yellow-400" />
                <div>
                  <div className="font-medium text-yellow-400">Payment Due</div>
                  <div className="text-sm text-gray-400">
                    You have ${billing.unpaidAmount?.toFixed(2)} outstanding.
                    {billing.daysUntilDeactivation !== null && (
                      <span className="text-yellow-400 ml-1">
                        {billing.daysUntilDeactivation} days until service deactivation.
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={makePayment}
                disabled={processingPayment}
                className="px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {processingPayment ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                Pay Now
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-400">Active Subscriptions</CardTitle>
            <CreditCard className="h-4 w-4 text-purple-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">
              {activeSubscriptions}/{profiles.length}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              ${totalSubscriptionCost.toFixed(2)}/month
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-gray-800">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-400">Minutes Used</CardTitle>
            <Clock className="h-4 w-4 text-blue-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">{totalMinutes.toFixed(1)}</div>
            <p className="text-xs text-gray-500 mt-1">
              {freeMinutesUsed.toFixed(1)} free / {freeMinutes} included
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-gray-800">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-400">Phone Numbers</CardTitle>
            <Phone className="h-4 w-4 text-amber-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">${totalPhoneCost.toFixed(2)}</div>
            <p className="text-xs text-gray-500 mt-1">Monthly phone costs</p>
          </CardContent>
        </Card>

        <Card className="bg-gray-900 border-gray-800">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-400">Total This Month</CardTitle>
            <DollarSign className="h-4 w-4 text-green-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-400">
              ${totalBill.toFixed(2)}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Subscriptions + phones + usage
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Invoice Breakdown */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-400" />
            Current Month Invoice
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Per-Profile Subscriptions */}
            <div className="flex items-center justify-between py-3 border-b border-gray-800">
              <div>
                <div className="text-white flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-gray-400" />
                  Business Profile Subscriptions ({activeSubscriptions})
                </div>
                <div className="text-sm text-gray-500">
                  {profiles.filter(p => p.subscription.isActive).map(p => p.name).join(', ') || 'No active subscriptions'}
                </div>
              </div>
              <div className="text-white font-medium">${totalSubscriptionCost.toFixed(2)}</div>
            </div>
            
            {/* Phone Numbers */}
            {totalPhoneCost > 0 && (
              <div className="flex items-center justify-between py-3 border-b border-gray-800">
                <div>
                  <div className="text-white flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    Phone Numbers ({profiles.reduce((sum, p) => sum + p.phoneNumbers, 0)})
                  </div>
                  <div className="text-sm text-gray-500">
                    Across all business profiles
                  </div>
                </div>
                <div className="text-white font-medium">${totalPhoneCost.toFixed(2)}</div>
              </div>
            )}
            
            <div className="flex items-center justify-between py-3 border-b border-gray-800">
              <div>
                <div className="text-white">Free Minutes</div>
                <div className="text-sm text-gray-500">
                  {freeMinutesUsed.toFixed(1)} / {freeMinutes} minutes used
                </div>
              </div>
              <div className="text-green-400 font-medium">Included</div>
            </div>
            
            {billableMinutes > 0 && (
              <div className="flex items-center justify-between py-3 border-b border-gray-800">
                <div>
                  <div className="text-white">Additional Usage</div>
                  <div className="text-sm text-gray-500">
                    {billableMinutes.toFixed(1)} min × ${pricePerMinute.toFixed(2)}/min
                  </div>
                </div>
                <div className="text-white font-medium">${usageCost.toFixed(2)}</div>
              </div>
            )}
            
            <div className="flex items-center justify-between py-3 bg-gray-800/50 rounded-lg px-4 -mx-4">
              <div className="text-lg font-medium text-white">Total Due</div>
              <div className="text-lg font-bold text-green-400">${totalBill.toFixed(2)}</div>
            </div>
            
            {totalBill > 0 && activeSubscriptions > 0 && (
              <button
                onClick={makePayment}
                disabled={processingPayment}
                className="w-full mt-4 px-4 py-3 bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {processingPayment ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                Pay ${totalBill.toFixed(2)} Now
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Autopay & Payment Method Card */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <RefreshCcw className="h-5 w-5 text-blue-400" />
            Automatic Billing
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {autopayStatus?.autopayEnabled ? (
              <>
                <div className="flex items-center justify-between p-4 bg-green-900/20 border border-green-500/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <div className="font-medium text-white">Autopay Enabled</div>
                      <div className="text-sm text-gray-400">
                        Your card will be charged automatically at the end of each billing cycle.
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={disableAutopay}
                    className="px-3 py-1.5 text-sm bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors"
                  >
                    Disable
                  </button>
                </div>
                
                {autopayStatus?.paymentMethod && (
                  <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                    <Wallet className="w-5 h-5 text-gray-400" />
                    <div>
                      <div className="text-sm text-white">
                        {autopayStatus.paymentMethod.brand?.toUpperCase()} •••• {autopayStatus.paymentMethod.last4}
                      </div>
                      <div className="text-xs text-gray-500">
                        Expires {autopayStatus.paymentMethod.expMonth}/{autopayStatus.paymentMethod.expYear}
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center justify-between p-4 bg-gray-800/50 border border-gray-700 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-gray-400" />
                  </div>
                  <div>
                    <div className="font-medium text-white">Enable Autopay</div>
                    <div className="text-sm text-gray-400">
                      Add a payment method to enable automatic monthly billing. No more manual payments!
                    </div>
                  </div>
                </div>
                <button
                  onClick={setupAutopay}
                  disabled={settingUpAutopay}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {settingUpAutopay ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CreditCard className="w-4 h-4" />
                  )}
                  Setup Autopay
                </button>
              </div>
            )}
            
            {/* Billing Status */}
            {autopayStatus?.billingStatus === 'grace_period' && (
              <div className="p-3 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
                <div className="flex items-center gap-2 text-yellow-400">
                  <AlertCircle className="w-4 h-4" />
                  <span className="font-medium">Payment Past Due</span>
                </div>
                <div className="text-sm text-gray-400 mt-1">
                  Your last payment failed. Please update your payment method or pay manually to avoid service interruption.
                  {autopayStatus.gracePeriodEnd && (
                    <span className="text-yellow-400 ml-1">
                      Grace period ends {new Date(autopayStatus.gracePeriodEnd).toLocaleDateString()}.
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Invoice History */}
      {invoices.length > 0 && (
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <FileText className="h-5 w-5 text-amber-400" />
              Recent Invoices
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {invoices.map((invoice) => (
                <div 
                  key={invoice._id || invoice.stripeInvoiceId}
                  className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${
                      invoice.status === 'paid' ? 'bg-green-400' :
                      invoice.status === 'payment_failed' ? 'bg-red-400' :
                      'bg-yellow-400'
                    }`} />
                    <div>
                      <div className="text-sm text-white">
                        {invoice.billingCycleStart && invoice.billingCycleEnd ? (
                          `${new Date(invoice.billingCycleStart).toLocaleDateString()} - ${new Date(invoice.billingCycleEnd).toLocaleDateString()}`
                        ) : (
                          new Date(invoice.createdAt).toLocaleDateString()
                        )}
                      </div>
                      <div className="text-xs text-gray-500 capitalize">{invoice.status}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-white font-medium">${invoice.amount?.toFixed(2)}</div>
                    {invoice.invoiceUrl && (
                      <a
                        href={invoice.invoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-400 hover:text-blue-300"
                      >
                        View
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plan Details */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-purple-400" />
            Pricing Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="text-sm text-gray-400 mb-1">Per Profile</div>
              <div className="text-2xl font-bold text-white">${subscriptionPricePerProfile.toFixed(2)}</div>
              <div className="text-xs text-gray-500 mt-1">Monthly subscription</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="text-sm text-gray-400 mb-1">Free Minutes</div>
              <div className="text-2xl font-bold text-white">{freeMinutes}</div>
              <div className="text-xs text-gray-500 mt-1">Per subscription</div>
            </div>
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="text-sm text-gray-400 mb-1">Overage Rate</div>
              <div className="text-2xl font-bold text-white">${pricePerMinute.toFixed(2)}/min</div>
              <div className="text-xs text-gray-500 mt-1">After free tier</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Usage History */}
      <Card className="bg-gray-900 border-gray-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Calendar className="h-5 w-5 text-blue-400" />
            Usage History {billingCycleActive ? '(Current Billing Cycle)' : '(Last 30 Days)'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!summary.dailyBreakdown || summary.dailyBreakdown.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No usage data available yet. Make some calls to see your usage here.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-800">
                    <th className="text-left py-3 text-sm font-medium text-gray-400">Date</th>
                    <th className="text-right py-3 text-sm font-medium text-gray-400">Total Calls</th>
                    <th className="text-right py-3 text-sm font-medium text-gray-400">Completed</th>
                    <th className="text-right py-3 text-sm font-medium text-gray-400">Minutes</th>
                    <th className="text-right py-3 text-sm font-medium text-gray-400">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.dailyBreakdown.map((day, idx) => (
                    <tr key={day.date || idx} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                      <td className="py-3 text-white">
                        {new Date(day.date).toLocaleDateString('en-US', { 
                          weekday: 'short', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </td>
                      <td className="py-3 text-right text-gray-300">{day.calls || 0}</td>
                      <td className="py-3 text-right text-gray-300">{day.completedCalls || 0}</td>
                      <td className="py-3 text-right text-gray-300">{day.minutes?.toFixed(1) || '0.0'}</td>
                      <td className="py-3 text-right text-green-400">${day.cost?.toFixed(2) || '0.00'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-gray-700 bg-gray-800/30">
                    <td className="py-3 text-white font-medium">Total</td>
                    <td className="py-3 text-right text-white font-medium">{summary.totalCalls || 0}</td>
                    <td className="py-3 text-right text-white font-medium">{summary.completedCalls || 0}</td>
                    <td className="py-3 text-right text-white font-medium">{summary.totalMinutes?.toFixed(1) || '0.0'}</td>
                    <td className="py-3 text-right text-green-400 font-medium">${summary.totalCost?.toFixed(2) || '0.00'}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
